/** 交互画布：绘制剪口、选择/移动/缩放、缩放与平移（含移动端双指）。 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CutShape, Vec } from '../domain/types';
import { paperGeometry } from '../domain/folds/geometry';
import { boundsOf, distance, simplify } from '../domain/geometry/polygon';
import { FoldedScene } from './Paint';
import { clientToWorld, hitTest, scaleCut, type Viewport } from './view';
import type { Action, EditorState, Tool } from './state';
import { viewBoxFromBounds } from './view';

type Gesture =
  | { kind: 'freehand'; points: Vec[] }
  | { kind: 'shape'; center: Vec; start: Vec }
  | { kind: 'move'; cutId: string; startWorld: Vec; original: CutShape }
  | { kind: 'resize'; cutId: string; startWorld: Vec; original: CutShape }
  | { kind: 'pinch'; startDist: number; startScale: number; startMid: Vec; startTx: number; startTy: number; startViewTx: number; startViewTy: number }
  | { kind: 'pan'; startClient: Vec; startTx: number; startTy: number };

const MIN_SHAPE_MM = 0.8;

export interface CanvasProps {
  state: EditorState;
  dispatch: React.Dispatch<Action>;
  notify: (kind: 'info' | 'warn' | 'error' | 'success', text: string) => void;
  onPreviewCutsChange?: (cuts: CutShape[] | null) => void;
}

export function Canvas({ state, dispatch, notify, onPreviewCutsChange }: CanvasProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const groupRef = useRef<SVGGElement>(null);
  const pointers = useRef(new Map<number, Vec>());
  const gesture = useRef<Gesture | null>(null);
  const [liveCut, setLiveCut] = useState<CutShape | null>(null);
  const [view, setView] = useState<Viewport>({ scale: 1, tx: 0, ty: 0 });
  const geometry = useMemo(
    () => paperGeometry(state.project.foldId, state.project.finishedSize),
    [state.project.foldId, state.project.finishedSize],
  );
  const editableBounds = useMemo(() => boundsOf(geometry.editable), [geometry.editable]);
  const viewBox = useMemo(() => viewBoxFromBounds(editableBounds, 10), [editableBounds]);

  const snap = useCallback(
    (p: Vec): Vec => {
      const step = state.project.guides.snapMm;
      if (!step) return p;
      return { x: Math.round(p.x / step) * step, y: Math.round(p.y / step) * step };
    },
    [state.project.guides.snapMm],
  );

  const toWorld = useCallback((clientX: number, clientY: number): Vec | null => {
    return clientToWorld(groupRef.current, clientX, clientY);
  }, []);

  // ---------------- 多边形工具：点击加点
  const addDraftPoint = useCallback(
    (p: Vec) => {
      const points = state.draft ?? [];
      if (points.length >= 3 && distance(points[0], p) < 2.2) {
        finishPolygon(points);
        return;
      }
      dispatch({ type: 'setDraft', points: [...points, snap(p)] });
    },
    [dispatch, snap, state.draft],
  );

  const finishPolygon = useCallback(
    (points?: Vec[]) => {
      const draft = points ?? state.draft;
      if (!draft || draft.length < 3) {
        notify('warn', '至少需要 3 个点才能构成剪口。');
        return;
      }
      dispatch({ type: 'addCut', cut: { id: crypto.randomUUID(), kind: 'polygon', points: draft } });
    },
    [dispatch, notify, state.draft],
  );

  const cancelDraft = useCallback(() => {
    dispatch({ type: 'setDraft', points: null });
    notify('info', '已取消本次绘制。');
  }, [dispatch, notify]);

  // 供工具栏调用的快捷键
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        dispatch({ type: e.shiftKey ? 'redo' : 'undo' });
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        dispatch({ type: 'redo' });
      } else if (e.key === 'Escape') {
        dispatch({ type: 'setDraft', points: null });
      } else if (e.key === 'Enter') {
        finishPolygon();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (state.selectedId) {
          e.preventDefault();
          dispatch({ type: 'removeCut', id: state.selectedId });
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dispatch, finishPolygon, state.selectedId]);

  // ---------------- 指针事件
  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    const pt = { x: e.clientX, y: e.clientY };
    pointers.current.set(e.pointerId, pt);
    (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId);

    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current = {
        kind: 'pinch',
        startDist: Math.max(1, distance(a, b)),
        startScale: view.scale,
        startMid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        startTx: view.tx,
        startTy: view.ty,
        startViewTx: view.tx,
        startViewTy: view.ty,
      };
      return;
    }

    const world = toWorld(e.clientX, e.clientY);
    if (!world) return;

    if (state.tool === 'select') {
      const id = hitTest(state.project.cuts, world);
      if (id) {
        dispatch({ type: 'select', id });
        const original = state.project.cuts.find((c) => c.id === id)!;
        gesture.current = { kind: 'move', cutId: id, startWorld: world, original };
      } else {
        dispatch({ type: 'select', id: null });
        gesture.current = { kind: 'pan', startClient: pt, startTx: view.tx, startTy: view.ty };
      }
      return;
    }

    if (state.tool === 'polygon') return; // 在 pointerup 处理点击

    if (state.tool === 'freehand') {
      gesture.current = { kind: 'freehand', points: [world] };
      return;
    }

    // 几何剪口：从中心拖出
    gesture.current = { kind: 'shape', center: snap(world), start: world };
  };

  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;

    if (g.kind === 'pinch') {
      if (pointers.current.size < 2) return;
      const [a, b] = [...pointers.current.values()];
      const dist = Math.max(1, distance(a, b));
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const nextScale = Math.min(8, Math.max(0.4, (g.startScale * dist) / g.startDist));
      setView({
        scale: nextScale,
        tx: g.startTx + (mid.x - g.startMid.x),
        ty: g.startTy + (mid.y - g.startMid.y),
      });
      return;
    }

    if (g.kind === 'pan') {
      setView({
        scale: view.scale,
        tx: g.startTx + (e.clientX - g.startClient.x),
        ty: g.startTy + (e.clientY - g.startClient.y),
      });
      return;
    }

    const world = toWorld(e.clientX, e.clientY);
    if (!world) return;

    if (g.kind === 'freehand') {
      const last = g.points[g.points.length - 1];
      if (!last || distance(last, world) > 0.4) g.points.push(world);
      setLiveCut({ id: 'live', kind: 'freehand', points: g.points });
      return;
    }

    if (g.kind === 'shape') {
      const radius = Math.max(MIN_SHAPE_MM, distance(g.center, world));
      const rotation = Math.atan2(world.y - g.center.y, world.x - g.center.x);
      if (state.tool === 'circle') {
        setLiveCut({ id: 'live', kind: 'circle', center: g.center, radius });
      } else if (state.tool === 'triangle') {
        setLiveCut({ id: 'live', kind: 'triangle', center: g.center, radius, rotation });
      } else if (state.tool === 'rect') {
        setLiveCut({
          id: 'live',
          kind: 'rect',
          center: g.center,
          width: Math.max(MIN_SHAPE_MM * 2, Math.abs(world.x - g.center.x) * 2),
          height: Math.max(MIN_SHAPE_MM * 2, Math.abs(world.y - g.center.y) * 2),
          rotation: 0,
        });
      }
      return;
    }

    if (g.kind === 'move') {
      const dx = world.x - g.startWorld.x;
      const dy = world.y - g.startWorld.y;
      setLiveCut(translateCut(g.original, dx, dy));
      return;
    }

    if (g.kind === 'resize') {
      setLiveCut(scaleCut(g.original, world, g.startWorld));
    }
  };

  const onPointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
    const world = toWorld(e.clientX, e.clientY);
    const g = gesture.current;
    pointers.current.delete(e.pointerId);

    // 多边形：没有拖拽手势，抬起即落点（必须在 g 判空之前处理）
    if (!g && state.tool === 'polygon' && world) {
      addDraftPoint(world);
      return;
    }
    if (!g) return;

    if (g.kind === 'pinch') {
      if (pointers.current.size === 0) gesture.current = null;
      return;
    }

    if (g.kind === 'pan') {
      gesture.current = null;
      return;
    }

    if (g.kind === 'freehand') {
      const simplified = simplify(g.points, 0.6);
      setLiveCut(null);
      gesture.current = null;
      if (simplified.length >= 3) {
        dispatch({ type: 'addCut', cut: { id: crypto.randomUUID(), kind: 'freehand', points: simplified } });
      } else {
        notify('warn', '笔迹太短，已忽略。请画一个大一点的区域。');
      }
      return;
    }

    if (g.kind === 'shape') {
      const cut = liveCut;
      setLiveCut(null);
      gesture.current = null;
      if (cut && cut.kind !== 'freehand' && cut.kind !== 'polygon') {
        dispatch({ type: 'addCut', cut: { ...cut, id: crypto.randomUUID() } });
      }
      return;
    }

    if (g.kind === 'move' || g.kind === 'resize') {
      const final = liveCut;
      setLiveCut(null);
      gesture.current = null;
      if (final && !sameCut(final, g.original)) {
        dispatch({ type: 'replaceCut', cut: final });
      }
      return;
    }

    gesture.current = null;
  };

  const onHandleDown = (e: React.PointerEvent, cutId: string) => {
    e.stopPropagation();
    const world = toWorld(e.clientX, e.clientY);
    const original = state.project.cuts.find((c) => c.id === cutId);
    if (!world || !original) return;
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    gesture.current = { kind: 'resize', cutId, startWorld: world, original };
  };

  const onWheel = (e: React.WheelEvent<SVGSVGElement>) => {
    e.preventDefault();
    const factor = e.deltaY > 0 ? 0.92 : 1.08;
    setView((v) => ({ ...v, scale: Math.min(8, Math.max(0.4, v.scale * factor)) }));
  };

  const resetView = () => setView({ scale: 1, tx: 0, ty: 0 });

  const cutsForRender = liveCut
    ? [...state.project.cuts.filter((c) => c.id !== liveCut.id), liveCut]
    : state.project.cuts;

  const projectForRender = { ...state.project, cuts: cutsForRender };

  // 把拖动中的临时剪口同步给展开预览，不必等到松手后才看到结果。
  useEffect(() => {
    if (!liveCut) {
      onPreviewCutsChange?.(null);
      return;
    }
    onPreviewCutsChange?.([
      ...state.project.cuts.filter((c) => c.id !== liveCut.id),
      liveCut,
    ]);
  }, [liveCut, onPreviewCutsChange, state.project.cuts]);

  useEffect(
    () => () => {
      onPreviewCutsChange?.(null);
    },
    [onPreviewCutsChange],
  );

  return (
    <div className="canvas-wrap">
      <svg
        ref={svgRef}
        className="canvas-svg"
        viewBox={viewBox}
        preserveAspectRatio="xMidYMid meet"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
        style={{ touchAction: 'none', cursor: cursorFor(state.tool) }}
      >
        <g ref={groupRef} transform={`translate(${view.tx} ${view.ty}) scale(${view.scale})`}>
          <FoldedScene
            project={projectForRender}
            draft={state.draft}
            selectedId={state.selectedId}
            showHandles={state.tool === 'select'}
            onHandleDown={onHandleDown}
          />
        </g>
      </svg>
      <div className="canvas-tools">
        <button type="button" className="ghost" onClick={() => setView((v) => ({ ...v, scale: Math.min(8, v.scale * 1.2) }))} aria-label="放大">＋</button>
        <button type="button" className="ghost" onClick={() => setView((v) => ({ ...v, scale: Math.max(0.4, v.scale / 1.2) }))} aria-label="缩小">－</button>
        <button type="button" className="ghost" onClick={resetView} aria-label="重置视图">重置</button>
      </div>
      {state.draft && state.draft.length > 0 && (
        <div className="draft-bar">
          <span>已放 {state.draft.length} 个点</span>
          <button type="button" onClick={() => finishPolygon()}>完成</button>
          <button type="button" className="ghost" onClick={cancelDraft}>取消</button>
        </div>
      )}
    </div>
  );
}

function cursorFor(tool: Tool): string {
  if (tool === 'select') return 'default';
  return 'crosshair';
}

function translateCut(cut: CutShape, dx: number, dy: number): CutShape {
  switch (cut.kind) {
    case 'polygon':
    case 'freehand':
      return { ...cut, points: cut.points.map((p) => ({ x: p.x + dx, y: p.y + dy })) };
    default:
      return { ...cut, center: { x: cut.center.x + dx, y: cut.center.y + dy } };
  }
}

function sameCut(a: CutShape, b: CutShape): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
